import { Helmet } from "react-helmet-async";

const SITE_NAME = "Hinton.";
const SITE_TAGLINE = "Your Source for Politics, Business, Culture and More";
const DEFAULT_DESCRIPTION =
  "Hinton. Your source for politics, business, culture and more, serious reporting and sharp cultural commentary from Britain.";
const DEFAULT_IMAGE = "https://hintononline.com/og-default.jpg";

function siteOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "https://hintononline.com";
}

function absoluteUrl(path) {
  if (!path) return siteOrigin();
  if (/^https?:\/\//i.test(path)) return path;
  return `${siteOrigin()}${path.startsWith("/") ? path : `/${path}`}`;
}

// Twitter's hard cap on OG images is 5 MB, Facebook's is 8 MB. Many of our
// uploaded cover JPGs are 5-10 MB, which causes X (Twitter) to silently drop
// the preview card. For images served by our own /api/files endpoint, we
// append ?w=1200 so the backend serves a resized, social-safe variant.
function socialSafeImage(url) {
  if (!url || typeof url !== "string") return url;
  const isOurFile = url.includes("/api/files/");
  if (!isOurFile) return url;
  // Don't double-append if a width is already requested.
  if (/[?&]w=\d+/.test(url)) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}w=1200&q=85`;
}

/**
 * Universal SEO component. Renders <head> tags for any page or article.
 *
 * Props:
 *  - title        : page title (full title is "X | Hinton.")
 *  - description  : meta description (155 chars ideal)
 *  - canonical    : absolute or path-only URL (defaults to current location)
 *  - image        : OG/Twitter image (absolute URL preferred)
 *  - type         : OG type ("website" | "article")
 *  - author       : article author display name
 *  - publishedAt  : ISO date string
 *  - updatedAt    : ISO date string
 *  - section      : article section / primary category
 *  - tags         : array of strings
 *  - noindex      : set true to add robots noindex
 *  - jsonLd       : pre-built JSON-LD object (will be JSON.stringified)
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  author,
  publishedAt,
  updatedAt,
  section,
  tags = [],
  noindex = false,
  jsonLd,
}) {
  // Title format:
  //  - With page title:    "{title} | Hinton."
  //  - Homepage (no title): "Hinton — {tagline}"  (brand keyword first for brand queries)
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `Hinton, ${SITE_TAGLINE}`;
  const url =
    typeof window !== "undefined" && window.location
      ? `${window.location.origin}${window.location.pathname}`
      : absoluteUrl("/");
  const canonicalUrl = canonical ? absoluteUrl(canonical) : url;
  const imageUrl = socialSafeImage(absoluteUrl(image));
  const desc = (description || DEFAULT_DESCRIPTION).slice(0, 300);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:locale" content="en_GB" />
      {/* Facebook App ID — leave blank for now; setting this later enables
          Domain Insights & higher OG fidelity. */}
      {type === "article" && author && <meta property="article:author" content={author} />}
      {type === "article" && publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {type === "article" && updatedAt && (
        <meta property="article:modified_time" content={updatedAt} />
      )}
      {type === "article" && section && <meta property="article:section" content={section} />}
      {type === "article" && tags.map((t) => (
        <meta key={t} property="article:tag" content={t} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@hintononline" />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

export { absoluteUrl, siteOrigin, SITE_NAME, DEFAULT_DESCRIPTION };
